import { useTranslation } from 'react-i18next'
import { Wifi, X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { Button } from '@/components/ui/Button.tsx'
import { useSlowConnectionDetector } from '@/lib/hooks/useSlowConnectionDetector'
import { useIsInteractionBlocked } from '@/lib/hooks/useIsInteractionBlocked'
import { useIsBottomSurfaceClaimedAbove } from '@/lib/hooks/bottomSurface'

/**
 * Suggestion tier: offered, not announced.
 *
 * This replaced a full-width `role="alert"` bar pinned to the top of the
 * viewport with a solid CTA button in it. Three things were wrong with
 * that, all measured 2026-08-26:
 *
 * 1. It reserved a 33px row of the app's chrome for a suggestion, and
 *    stacked with the other status bars.
 * 2. At 375px its description collapsed to ZERO width in all four locales
 *    (title and CTA are `shrink-0`), so the sentence explaining the offer
 *    was simply gone on a phone — in Russian the title plus button alone
 *    (220px + 170px) already exceed the viewport.
 * 3. With a modal dialog open it stayed fully visible while Radix's
 *    `pointer-events: none` on `<body>` made its button un-clickable.
 *
 * So it moves out of the chrome and into the bottom corner, keeps clear of
 * the mobile FAB, hides itself while a modal owns the screen, and takes
 * "no" for an answer for a day (`slowConnectionSnooze.ts`).
 *
 * z-index 92 is deliberate and sits between two neighbours: above
 * `MobileFab` (90) so the chip is never half-covered, below
 * `PushPromptCard` (95) and `NativeVersionUpdateCard` (120). A suggestion
 * about connection quality yields to anything with more to say.
 */
export default function SlowConnectionPrompt() {
  const { t } = useTranslation()
  const { isPromptVisible, dismiss, switchToOfflineMode } = useSlowConnectionDetector()
  const isInteractionBlocked = useIsInteractionBlocked()
  const isOutranked = useIsBottomSurfaceClaimedAbove('slow-connection')

  if (!isPromptVisible || isInteractionBlocked || isOutranked) return null

  return (
    <section
      // `status`, not `alert`: nothing is wrong and nothing is urgent.
      role="status"
      data-cy="slow-connection-prompt"
      className={cn(
        'fixed left-3 z-[92] rounded-lg border border-amber-200 bg-white p-3 shadow-lg',
        'dark:border-amber-900 dark:bg-zinc-950',
        // Mobile: bottom-left, capped so the 56px FAB at `right-4` stays fully
        // uncovered no matter how long the translated title runs.
        'bottom-[calc(var(--bottomTabBarHeight)_+_var(--safe-area-inset-bottom)_+_0.75rem)]',
        'max-w-[calc(100%_-_5.5rem)]',
        'md:bottom-4 md:left-auto md:right-4 md:w-[340px] md:max-w-none',
      )}
    >
      <div className="flex items-start gap-2.5">
        <Wifi
          className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{t('offlineMode.banner.slowDetected')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('offlineMode.banner.slowDescription')}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent"
          aria-label={t('offlineMode.banner.slowDismiss')}
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="mt-2.5 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={dismiss}>
          {t('offlineMode.banner.slowDismiss')}
        </Button>
        <Button type="button" size="sm" onClick={switchToOfflineMode}>
          {t('offlineMode.banner.switchToOffline')}
        </Button>
      </div>
    </section>
  )
}
